import { DefaultPageHeader } from 'layouts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { CardTabs, Col, RightLine, Row } from 'ui';
import { isDexcomEnabled } from 'common/validators';
import type { StatusType } from 'features/dexcom';
import { ChangePassword } from './change-password';
import { DexcomSync } from './dexcom-sync';
import { Passkeys } from './passkeys';
import { RemoveAccount } from './remove-account';
import { SettingsNotifications } from './settings-notifications';
import { type SettingsParams, isSettingsScope } from './types';
import { UserTranslations } from './user-translations';

type P = {
  dexcomSync: {
    automaticSync?: boolean;
    onFinished: ({ messageText, status }: { messageText: string; status: StatusType }) => void;
  };
  logOut: () => void;
  tabOptions: SettingsParams;
};

const Settings = ({ dexcomSync, logOut, tabOptions }: P) => {
  const { t } = useTranslation();
  const { canViewPasskeys } = usePermissions();

  const items = useMemo(() => {
    const baseItems = [
      {
        children: <SettingsNotifications />,
        icon: <RightLine />,
        key: 'notifications',
        label: t('Notifications'),
      },
      {
        children: <UserTranslations />,
        icon: <RightLine />,
        key: 'language',
        label: t('Languages'),
      },
    ];

    if (isDexcomEnabled()) {
      baseItems.push({
        children: (
          <DexcomSync
            automaticSync={dexcomSync.automaticSync}
            onFinished={({ messageText, status }) => {
              dexcomSync.onFinished({ messageText, status });
            }}
          />
        ),
        icon: <RightLine />,
        key: 'dexcom-sync',
        label: t('Dexcom Sync'),
      });
    }

    baseItems.push({
      children: <ChangePassword />,
      icon: <RightLine />,
      key: 'change-password',
      label: t('Change password'),
    });

    if (canViewPasskeys({})) {
      baseItems.push({
        children: <Passkeys />,
        icon: <RightLine />,
        key: 'passkeys',
        label: t('Passkeys'),
      });
    }

    baseItems.push({
      children: <RemoveAccount logOut={logOut} />,
      icon: <RightLine />,
      key: 'remove-account',
      label: t('Remove account'),
    });

    return baseItems;
  }, [canViewPasskeys, dexcomSync, logOut, t]);

  return (
    <>
      <DefaultPageHeader title="Settings" />
      {/* TODO: there is a problem with tabs height if they are not in the row */}
      <Row>
        <Col>
          <CardTabs
            card={{ marginBottom: false }}
            tabs={{
              activeKey: tabOptions.tab,
              items,
              onChange(activeTabKey) {
                isSettingsScope(activeTabKey) && tabOptions.onChangeTab(activeTabKey);
              },
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default Settings;
